import type { location } from '@cwos/api-client';

/**
 * THE WORDS FOR "WHAT WAS THIS WORKER TOLD, AND WHAT GOVERNED THEIR SHIFT".
 *
 * Pure — no React, no dates of its own — so every branch is asserted directly.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS FILE IS ANSWERING, AND WHY IT IS NOT A SETTINGS PAGE
 * ═══════════════════════════════════════════════════════════════════════════
 * The founder's shift-binding ruling of 2026-09-08 ends:
 *
 *   "Preserve the policy version/effective timestamp associated with the shift
 *    so Phibian can later establish what the worker was told and what
 *    collection rules governed that shift."
 *
 *   "`effective_from` must therefore be real domain behaviour, not dead
 *    storage."
 *
 * A binding nobody can read is dead storage with extra steps. `GET
 * /location/sessions/:id/policy-binding` has answered this in full since
 * migration 1962000000000 and NO screen read it, so the ruling's own evidence
 * clause was unsatisfiable by anyone but a developer with database access.
 * These sentences are the answer to that question about ONE past shift; the
 * organization's forward-looking "when does my change land" is a different
 * question and already has a surface (`location-settings/PolicySchedule.tsx`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NOTHING HERE RE-DERIVES PRECEDENCE
 * ═══════════════════════════════════════════════════════════════════════════
 * The server decides which version governed, which way collection moved, and
 * which floors a change is still waiting on; `direction` and `explanation`
 * arrive as its own prose and are RENDERED, never recomputed. Three of the ten
 * policy axes invert (a shorter breadcrumb interval and a tighter accuracy
 * floor are MORE collection; `allow_attested_override: false` is more
 * monitoring), so a screen comparing two policies for itself would get the sign
 * wrong on a third of them and look right.
 *
 * What this file owns is only the mapping from the server's closed vocabularies
 * to English, and those maps are CLOSED IN BOTH DIRECTIONS: an unrecognised
 * value renders as an explicit unknown naming the value, never as a blank and
 * never as the nearest neighbour. A vocabulary member silently dropped reads as
 * a state that did not happen.
 */

/**
 * THE FLOORS, SAID FOR ONE SHIFT RATHER THAN FOR AN ORGANIZATION.
 *
 * `location-settings/copy.ts` has its own `FLOOR_LABELS` and they are
 * deliberately NOT reused: it says "the end of the shift each worker is already
 * in", which is the correct sentence on a settings form addressing a whole
 * scope and the wrong one here, where the reader is looking at ONE named
 * worker's ONE shift. The two maps carry the same keys and different words on
 * purpose. Every map in this file is a `Record` over the client union rather
 * than a loose object, so a member added to the wire vocabulary fails the
 * admin's own typecheck here instead of rendering as a blank.
 */
const FLOOR_LABELS: Record<location.GovernanceFloor, string> = {
  schedule: 'the time the operator scheduled it for',
  shift_boundary: 'the end of this shift',
};

export function floorLabel(floor: string): string {
  return (
    FLOOR_LABELS[floor as location.GovernanceFloor] ??
    `a condition this screen does not recognise (${floor})`
  );
}

/**
 * WHY A GIVEN STRETCH OF THE SHIFT WAS GOVERNED BY WHAT IT WAS GOVERNED BY.
 *
 * `bound_to_shift` and `waiting_for_schedule` produce identical governance and
 * get different sentences, because they are held back by different authorities:
 * the WORKER's floor (the ruling's ratchet — an increase waits for a shift
 * boundary and a disclosure) and the PUBLISHER's own schedule. An operator
 * asking "why did my change not reach this shift" needs to be told which, and a
 * reader who is told the wrong one will go and change the wrong thing.
 */
const BASIS_SENTENCES: Record<location.GoverningBasis, string> = {
  unbound:
    'No disclosure was recorded for this shift, so nothing was bound and the organization’s ' +
    'policy of the moment governed it.',
  bound_to_shift:
    'The version this shift was bound to. A change that increases collection waits for the end ' +
    'of a shift already running, so it did not reach this one.',
  waiting_for_schedule:
    'The version this shift was bound to. A change that would otherwise have applied at once ' +
    'was scheduled by its operator for later, so it had not begun.',
  reduction_applied_immediately:
    'A change that reduced or disabled collection, which the ruling lets take effect during a ' +
    'shift already running.',
  unchanged: 'A new version was published with the same collection rules, so nothing changed.',
};

export function basisSentence(basis: string): string {
  return (
    BASIS_SENTENCES[basis as location.GoverningBasis] ??
    `Phibian recorded a reason this screen does not recognise (${basis}).`
  );
}

/**
 * HOW MUCH OF THE TIMELINE IS READ AND HOW MUCH IS RECONSTRUCTED — printed
 * every time, not only when it is bad news.
 *
 * `unknown` is the one that must never be silent: it arrives with an EMPTY
 * timeline, and an empty list with no word for why is the "0 because we did not
 * look" the four-state rule exists to forbid.
 */
const COMPLETENESS_SENTENCES: Record<location.BindingCompleteness, string> = {
  exact:
    'Read, not reconstructed: no policy version came into force for this scope while the shift ' +
    'was running.',
  replayed:
    'Reconstructed: versions came into force during the shift, and each one was replayed through ' +
    'the same rule the live gate applies.',
  scope_change_possible:
    'Reconstructed, with a limit: a version also came into force in the other scope — a program ' +
    'override beside an organization policy, or the reverse — so which layer resolved may itself ' +
    'have changed during the shift. What follows reconstructs the bound scope only.',
  unknown:
    'Nothing to reconstruct: no disclosure was recorded for this shift, so Phibian cannot say ' +
    'what governed it.',
};

export function completenessSentence(completeness: string): string {
  return (
    COMPLETENESS_SENTENCES[completeness as location.BindingCompleteness] ??
    `Phibian recorded a completeness this screen does not recognise (${completeness}).`
  );
}

/**
 * WHAT THE WORKER WAS SHOWN — three answers, and the last two are the ones that
 * go wrong quietly.
 *
 * `product_default` is a REAL, MEASURED answer: the tenant had stored no policy
 * and Phibian's documented default governed, and we can say exactly what it
 * said. `not_recorded` is an ABSENCE: the shift was opened by a path that
 * disclosed nothing to anybody — a supervisor entry, an import — so there is no
 * answer to give. Rendering the second as the first tells a reader a worker was
 * shown something they were not.
 */
export function disclosureHeading(state: string): string {
  switch (state) {
    case 'version':
      return 'What this worker was shown';
    case 'product_default':
      return 'What this worker was shown: Phibian’s product default';
    case 'not_recorded':
      return 'What this worker was shown is not recorded';
    default:
      return 'What this worker was shown';
  }
}

export function disclosureSentence(disclosed: location.SessionDisclosure): string {
  switch (disclosed.state) {
    case 'version':
      return disclosed.source === 'program'
        ? `Version ${disclosed.version} of this worker’s program policy, bound to the shift when they clocked in.`
        : `Version ${disclosed.version} of the organization policy, bound to the shift when they clocked in.`;
    case 'product_default':
      return (
        'The organization had stored no location policy when this shift began, so Phibian’s ' +
        'product default governed it. That is a real answer and not a missing one — the default ' +
        'is deliberately not stored, so it cannot drift.'
      );
    case 'not_recorded':
      return (
        'This shift was not opened through the clock-in path, so no disclosure was recorded ' +
        'against it. That is different from a shift that was shown nothing: Phibian does not ' +
        'know what this worker was told.'
      );
    default:
      return 'Phibian recorded an answer this screen does not recognise.';
  }
}

/**
 * WHAT THE OPERATOR STATED, KEPT APART FROM WHAT THE SYSTEM CONCLUDED.
 *
 * "No schedule stated" and "scheduled for now" govern identically and are not
 * the same evidence — the ruling gives a reduction two legitimate behaviours
 * ("may apply immediately UNLESS EXPLICITLY SCHEDULED FOR LATER"), so choosing
 * now is a publisher's act and having chosen nothing is not. The form that
 * writes these keeps them apart; so does the screen that reads them back.
 *
 * @param when the stated instant, already formatted by the caller
 */
export function statedScheduleSentence(schedule: location.StatedSchedule, when: string): string {
  if (schedule.state === 'unscheduled') {
    return 'The operator stated no schedule, so this version governed from the moment it was recorded.';
  }
  return schedule.clampedToRecordedAt
    ? `The operator stated ${when}, which had already passed when the values were recorded. A ` +
        'publisher cannot backdate a disclosure into a shift that has already run, so it governed ' +
        'from the recording instant instead.'
    : `The operator scheduled this version for ${when}.`;
}

/**
 * WHEN A PUBLISHED-AND-WAITING CHANGE REACHES THIS WORKER.
 *
 * `governsForThisWorkerFrom` is null when an applicable floor has not happened
 * — while this worker's shift is still open, the maximum of the two floors is
 * UNDETERMINED. It is not "now", it is not the schedule on its own, and a
 * screen printing either of those would tell a reader a tracking change is
 * already live on a shift the ruling is keeping it out of.
 *
 * @param when the resolved instant, already formatted, or null for undetermined
 */
export function pendingArrivalSentence(when: string | null): string {
  return when === null
    ? 'When it reaches this worker cannot be named yet: it waits for the end of a shift that has ' +
        'not ended.'
    : `It governs this worker from ${when}.`;
}
